import { ArrowDown, ArrowUp, ArrowUpDown, GripVertical } from "lucide-react";
import * as React from "react";
import type { LinkProps } from "react-router";

import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

// Standard table "chrome" + the three row-behavior tiers (see DESIGN.md › Components › Tables).
// Setting `variant` makes the Table a self-contained rounded/bordered surface (NO outer
// <Card> needed — that's what caused the corner-clip bug) with a shaded, non-hovering
// header and 16px cell padding. Row hover/cursor appears ONLY when the row itself is
// clickable:
//   navigation  — whole row is a real link to a detail route via <TableCellLink>
//                 (row hover + pointer; Cmd/Ctrl/middle-click open a new tab)
//   interactive — whole row click → in-page action (select/expand/edit) (row hover + pointer)
//   data        — row is NOT clickable; read-only, or only cell-level controls
//                 (action-icon buttons, inline links).                  (no row hover)
// Omitting `variant` keeps the legacy bare table (unchanged), so existing tables that still
// wrap themselves in <Card> are unaffected until migrated.
type TableVariant = "navigation" | "interactive" | "data";

// Shaded, non-hovering header + 16px cells — applied for every variant.
const variantHeaderCells =
  "[&_thead]:bg-muted [&_thead_tr]:hover:bg-transparent [&_th]:px-4 [&_td]:px-4";
// Row interaction per tier (hover comes from the TableRow default; data turns it off).
const variantRows: Record<TableVariant, string> = {
  navigation: "[&_tbody_tr]:cursor-pointer",
  interactive: "[&_tbody_tr]:cursor-pointer",
  data: "[&_tbody_tr]:hover:bg-transparent",
};
// The self-contained surface (rounded border that clips the shaded header). `overflow-x-auto`
// still clips to the rounded corners, but when the table's minimum width (nowrap actions
// column, unbreakable codes) exceeds the container it scrolls sideways instead of silently
// cutting off the last column.
const variantSurface = "rounded-lg border bg-card overflow-x-auto";

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  variant?: TableVariant;
  /**
   * When `variant` is set, render the bordered/rounded surface. Default `true` for
   * standalone tables (top-level lists, or a widget whose only content is the table).
   * Set `false` for a table embedded inside a titled section `<Card>` so it doesn't
   * get a second border (box-in-box) — it still gets the shaded header, padding and
   * row behavior.
   */
  surface?: boolean;
  /** Classes for the wrapping container (e.g. max-height / loading opacity). */
  containerClassName?: string;
}

// `isolate` gives every table its own stacking context, so the `z-10` that lifts row
// controls above a row link (`rowControl`) can't paint over sticky page headers outside it.
const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, variant, surface = true, containerClassName, ...props }, ref) => (
    <div
      className={cn(
        "relative isolate w-full",
        variant
          ? cn(
              variantHeaderCells,
              variantRows[variant],
              surface ? variantSurface : "overflow-x-auto",
            )
          : "overflow-auto",
        containerClassName,
      )}
    >
      <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
  ),
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      // `relative` (for every variant) anchors a <TableCellLink>/<TableRowLink>'s stretched
      // overlay to its own row; without it the overlay would cover the whole table.
      // The identity transform does the same job in WebKit 26 (Safari 26.x on macOS/iOS/
      // iPadOS), which ignores `position: relative` on a <tr>: without it every row's
      // overlay covered the table and the last row won every click (QL-747). Keep both:
      // Firefox relies on `relative`, WebKit 26 on the transform. The transform also makes
      // every row a stacking context: overlays that must escape a row (menus, autocompletes)
      // need a Portal; a non-portalled absolute element in a cell is painted under later rows.
      // It also re-anchors `position: fixed` descendants to the row instead of the viewport,
      // so a non-portalled fixed overlay in a cell sticks to its row.
      className={cn(
        "relative [transform:translate(0)] border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
));
TableCaption.displayName = "TableCaption";

// Sortable header cell (DESIGN.md › Tables › Sortable headers): an optional add-on,
// orthogonal to variant. Pair with the `useTableSort` hook, which supplies
// `direction` (null when the column is inactive) and the click handler.
interface SortableTableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** Sort direction when this column is active, null otherwise. */
  direction: "asc" | "desc" | null;
  onSort: () => void;
}

const SortableTableHead = React.forwardRef<HTMLTableCellElement, SortableTableHeadProps>(
  ({ direction, onSort, className, children, ...props }, ref) => (
    <TableHead
      ref={ref}
      className={cn("cursor-pointer select-none", className)}
      onClick={onSort}
      aria-sort={direction === "asc" ? "ascending" : direction === "desc" ? "descending" : "none"}
      {...props}
    >
      {children}
      {direction === "asc" ? (
        <ArrowUp className="ml-1 inline h-3 w-3" />
      ) : direction === "desc" ? (
        <ArrowDown className="ml-1 inline h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-40" />
      )}
    </TableHead>
  ),
);
SortableTableHead.displayName = "SortableTableHead";

// Leading grip cell for drag-to-reorder rows (DESIGN.md › Tables › Reorderable rows).
// Pair with the `useDragReorder` hook, which owns the row's drag handlers.
const DragHandleCell = ({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
  <TableCell className={cn("w-12", className)} {...props}>
    <GripVertical className="h-4 w-4 text-muted-foreground" />
  </TableCell>
);
DragHandleCell.displayName = "DragHandleCell";

// Row-actions column (DESIGN.md › Tables › Conventions › "Actions column never wraps").
// `w-px` + `whitespace-nowrap` is the shrink-to-fit trick for auto table layout: the column
// is exactly as wide as its widest cluster of icon buttons and the text columns absorb the
// remaining width (and wrap) instead of the buttons breaking onto a second line.
const actionsColumn = "w-px whitespace-nowrap text-right";
// Lifts a cell's controls above a `navigation` row's stretched <TableCellLink> overlay.
const rowControl = "relative z-10";

const TableActionsHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <TableHead ref={ref} className={cn(actionsColumn, className)} {...props} />
));
TableActionsHead.displayName = "TableActionsHead";

// The cell lays its children out as a right-aligned `gap-1.5` flex cluster with `shrink-0`
// so icon buttons neither wrap nor squash. Clicks inside it never bubble to the row, so an
// `interactive` row doesn't fire its row click when an action is used, and it is raised
// above a `navigation` row's stretched link (`rowControl`).
const TableActionsCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, children, onClick, ...props }, ref) => (
  <TableCell
    ref={ref}
    className={cn(actionsColumn, rowControl, className)}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(e);
    }}
    {...props}
  >
    <div className="flex items-center justify-end gap-1.5 [&>*]:shrink-0">{children}</div>
  </TableCell>
));
TableActionsCell.displayName = "TableActionsCell";

// Row link for `navigation` tables (DESIGN.md › Tables › Navigation rows). A real
// <Link> whose ::after overlay stretches over the whole (relative) row, so the
// entire row is one native link: Cmd/Ctrl/middle-click and the context menu open a new tab,
// the URL shows on hover, and Tab + Enter work. Use exactly one per row, on the primary
// (name) cell, instead of `onClick={() => navigate(…)}` on the row. `TableCellLink` is the
// cell-sized shorthand; use `TableRowLink` directly when the cell also holds a control (e.g.
// a badge button), which must stay OUTSIDE the <a>.
// Any other control in the row must sit above the overlay: `TableActionsCell` already does;
// give other cells with checkboxes/switches/buttons/tooltips `className={rowControl}`.
// When the link has no readable text (e.g. an icon cell), pass `label` to TableCellLink
// (`aria-label` to TableRowLink). Don't repeat a value another cell already reads out.
const TableRowLink = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, ...props }, ref) => (
    <Link
      ref={ref}
      className={cn(
        "outline-none after:absolute after:inset-0 focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-ring",
        className,
      )}
      {...props}
    />
  ),
);
TableRowLink.displayName = "TableRowLink";

interface TableCellLinkProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  to: LinkProps["to"];
  state?: LinkProps["state"];
  /** Accessible name for the link when its content isn't readable text (e.g. an icon). */
  label?: string;
  /** Applied to the link, not the cell — pass `false` inside a draggable (reorderable) row. */
  draggable?: boolean;
  linkClassName?: string;
}

const TableCellLink = React.forwardRef<HTMLTableCellElement, TableCellLinkProps>(
  ({ to, state, label, linkClassName, draggable, children, ...props }, ref) => (
    <TableCell ref={ref} {...props}>
      <TableRowLink
        to={to}
        state={state}
        aria-label={label}
        className={linkClassName}
        draggable={draggable}
      >
        {children}
      </TableRowLink>
    </TableCell>
  ),
);
TableCellLink.displayName = "TableCellLink";

export {
  DragHandleCell,
  rowControl,
  SortableTableHead,
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCaption,
  TableCell,
  TableCellLink,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableRowLink,
};
